import {InlineDatumReactComponentProps} from '@stripe-internal/inline-data';
import {view, Icon, Tooltip} from '@sail/ui';
import {checkCircleFilled} from '@sail/icons/react/Icon';
import {defineMessage, FormattedMessage} from 'react-intl';

const messages = {
  getYouStarted: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.get_you_started',
    description: 'Get you started message',
    defaultMessage: 'I set up a few things to get you started.',
  }),
  continueCustomize: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.continue_customize',
    description: 'Continue customizing message',
    defaultMessage:
      'Complete the implementation steps when you’re ready to go live.',
  }),
  pullInBrandingStyles: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.pull_in_branding_styles',
    description: 'Pull branding styles',
    defaultMessage: 'Applied <Tooltip>styles</Tooltip> from your website',
  }),
  brandingStylesTooltip: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.branding_styles_tooltip',
    description: 'Branding styles tooltip',
    defaultMessage:
      'Logos, colors, and fonts from your website have been applied to your checkout to create a consistent brand experience.',
  }),
  addedPaymentMethodLogic: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.added_payment_method_logic',
    description: 'Added payment method logic',
    defaultMessage: 'Added <Tooltip>payment method logic</Tooltip>',
  }),
  paymentMethodLogicTooltip: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.payment_method_logic_tooltip',
    description: 'Payment method logic tooltip',
    defaultMessage:
      'Customers automatically see their preferred payment methods first based on location, local currency, device, and other signals.',
  }),
  tailoredToPricingModel: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.tailored_to_pricing_model',
    description: 'Tailored to your pricing model',
    defaultMessage: 'Tailored to <Tooltip>your pricing model</Tooltip>',
  }),
  pricingModelTooltip: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.pricing_model_tooltip',
    description: 'Pricing model tooltip',
    defaultMessage:
      'Your checkout automatically adjusts pricing displays, button text, and payment methods to match your pricing model.',
  }),
  enabledLocalizedPricing: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.enabled_localized_pricing',
    description: 'Enabled localized pricing',
    defaultMessage: 'Enabled <Tooltip>localized pricing</Tooltip>',
  }),
  enabledLocalizedPricingTooltop: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.enabled_localized_pricing_tooltip',
    description: 'Enabled localized pricing tooltip',
    defaultMessage:
      'Adaptive Pricing lets customers pay in their local currency, which can increase international revenue by up to 17%.',
  }),
  inspectIntro: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.inspect_intro',
    description: 'Inspect mode intro message',
    defaultMessage:
      "I've used your current account settings and most recent checkout session for this preview.",
  }),
  inspectDetails: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.inspect_details',
    description: 'Inspect mode details message',
    defaultMessage:
      'Explore which payment methods were shown, which were filtered out, and why\u2014along with the specific customer details and currency used.',
  }),
  inspectQuestion: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.inspect_question',
    description: 'Inspect mode question',
    defaultMessage: 'What would you like to know?',
  }),
  builderExistingUserPreview: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.builder_existing_user_preview',
    description: 'Builder mode existing user preview message',
    defaultMessage: "I've used your current account settings for this preview.",
  }),
  builderExistingUserEditPrompt: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.builder_existing_user_edit_prompt',
    description: 'Builder mode existing user edit prompt',
    defaultMessage:
      "Customize it however you'd like, then complete the implementation steps when you're ready to go live.",
  }),
  liveEditPreview: defineMessage({
    id: 'mse_inline_data.checkout_studio_greeting.live_edit_preview',
    description: 'Live edit mode preview message',
    defaultMessage:
      "I've used your current account settings and most recent checkout session for this preview.",
  }),
};

const TooltipValue = ({
  tooltipMessage,
}: {
  tooltipMessage: {id: string; defaultMessage: string; description: string};
}): ((label: React.ReactNode) => React.ReactElement) => {
  return (label: React.ReactNode) => (
    <Tooltip
      placement="top"
      trigger={<view.span css={{color: 'text.default'}}>{label}</view.span>}
    >
      <FormattedMessage {...tooltipMessage} />
    </Tooltip>
  );
};

const BuilderNewUserGreeting = (
  props: InlineDatumReactComponentProps,
): React.ReactElement => {
  const {datum} = props;

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <FormattedMessage {...messages.getYouStarted} />
      <view.div css={{stack: 'y', gap: 'small'}}>
        {datum?.value?.branding_style_is_eligible ? (
          <view.div css={{stack: 'x', gap: 'small'}}>
            <Icon icon={checkCircleFilled} size="xsmall" />
            <FormattedMessage
              {...messages.pullInBrandingStyles}
              values={{
                Tooltip: TooltipValue({
                  tooltipMessage: messages.brandingStylesTooltip,
                }),
              }}
            />
          </view.div>
        ) : null}
        <view.div css={{stack: 'x', gap: 'small'}}>
          <Icon icon={checkCircleFilled} size="xsmall" />
          <FormattedMessage
            {...messages.addedPaymentMethodLogic}
            values={{
              Tooltip: TooltipValue({
                tooltipMessage: messages.paymentMethodLogicTooltip,
              }),
            }}
          />
        </view.div>
        <view.div css={{stack: 'x', gap: 'small'}}>
          <Icon icon={checkCircleFilled} size="xsmall" />
          <FormattedMessage
            {...messages.tailoredToPricingModel}
            values={{
              Tooltip: TooltipValue({
                tooltipMessage: messages.pricingModelTooltip,
              }),
            }}
          />
        </view.div>
        <view.div css={{stack: 'x', gap: 'small'}}>
          <Icon icon={checkCircleFilled} size="xsmall" />
          <FormattedMessage
            {...messages.enabledLocalizedPricing}
            values={{
              Tooltip: TooltipValue({
                tooltipMessage: messages.enabledLocalizedPricingTooltop,
              }),
            }}
          />
        </view.div>
      </view.div>
      <FormattedMessage
        {...messages.continueCustomize}
        values={{
          b: (chunks: React.ReactNode) => <view.b>{chunks}</view.b>,
        }}
      />
    </view.div>
  );
};

const BuilderExistingUserGreeting = (): React.ReactElement => {
  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <FormattedMessage {...messages.builderExistingUserPreview} />
      <FormattedMessage {...messages.builderExistingUserEditPrompt} />
    </view.div>
  );
};

const LiveEditGreeting = (): React.ReactElement => {
  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <FormattedMessage {...messages.liveEditPreview} />
      <FormattedMessage {...messages.builderExistingUserEditPrompt} />
    </view.div>
  );
};

const InspectGreeting = (): React.ReactElement => {
  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <FormattedMessage {...messages.inspectIntro} />
      <FormattedMessage {...messages.inspectDetails} />
      <FormattedMessage {...messages.inspectQuestion} />
    </view.div>
  );
};

export const CheckoutStudioGreeting = (
  props: InlineDatumReactComponentProps,
): React.ReactElement => {
  const {datum} = props;
  const mode = datum?.value?.checkout_studio_mode;
  const isNewUser = datum?.value?.is_new_checkout_studio_user;

  if (mode === 'inspect') {
    return <InspectGreeting />;
  }

  if (mode === 'live_edit') {
    return <LiveEditGreeting />;
  }

  if (isNewUser) {
    return <BuilderNewUserGreeting {...props} />;
  }

  return <BuilderExistingUserGreeting />;
};
